import {
  Activity,
  ChevronRight,
  Clapperboard,
  HardDrive,
  LogOut,
  MapPin,
  Plane,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { get, put, type Me, type Trip } from "../lib/api.ts";
import { Link, navigate, tripPath, type TripSection } from "../lib/router.tsx";
import { isAdmin } from "../lib/rights.ts";
import { setPhoneLocation, signOut } from "../lib/session.ts";
import { Card, ICON, reason } from "../ui/index.ts";
import { PhoneSetupEntry } from "./PhoneSetupPage.tsx";
import { YouCard } from "./YouCard.tsx";

type Entry = { section: TripSection; label: string; hint: string; icon: typeof Users };

/**
 * The settings tab. It carries the sections a traveller does not touch mid-trip, plus the two
 * trip-level controls that used to live on the overview: the capture lifecycle and own sharing.
 */
export function SettingsPage({
  trip,
  me,
  lifecycle,
}: {
  trip: Trip;
  me: Me;
  lifecycle: (action: "START" | "END") => Promise<void>;
}) {
  const admin = isAdmin(trip.roles);
  const entries: Entry[] = [
    {
      section: "deliverables",
      label: "Deliverables",
      hint: "What the trip owes, and its state",
      icon: Clapperboard,
    },
    {
      section: "people",
      label: "People & Sync",
      hint: "Who is on the trip and what they have sent",
      icon: Users,
    },
    ...(admin
      ? [
          {
            section: "storage" as const,
            label: "Storage",
            hint: "Where the footage lands, and how much is left",
            icon: HardDrive,
          },
        ]
      : []),
    { section: "activity", label: "Activity", hint: "Everything that happened", icon: Activity },
  ];

  return (
    <div className="stack">
      <TripSwitcher trip={trip} me={me} />

      <YouCard trip={trip} me={me} />

      {admin && (trip.state === "DRAFT" || trip.state === "ACTIVE") && (
        <Card title="Capture">
          <p className="muted small" style={{ margin: 0 }}>
            {trip.state === "DRAFT"
              ? "Nothing is collected until the trip starts."
              : "Ending capture stops new footage; what is already up stays."}
          </p>
          <div className="row wrap">
            {trip.state === "DRAFT" ? (
              <button className="button primary" onClick={() => void lifecycle("START")}>
                Start trip
              </button>
            ) : (
              <button className="button" onClick={() => void lifecycle("END")}>
                End capture
              </button>
            )}
          </div>
        </Card>
      )}

      <PhoneSetupEntry />

      <Card title="This trip" label="This trip">
        <ul className="tile-list">
          {entries.map(({ section, label, hint, icon: Icon }) => (
            <li key={section}>
              <Link className="tile" to={tripPath(trip.id, section)}>
                <span className="tile-mark">
                  <Icon {...ICON} aria-hidden="true" />
                </span>
                <span className="tile-text">
                  <span className="tile-label">{label}</span>
                  <span className="tile-hint">{hint}</span>
                </span>
                <ChevronRight {...ICON} aria-hidden="true" className="tile-go" />
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      {/* Where the trip's places and categories are managed, and the Google import with them:
          only for a trip that has the map layers. */}
      {trip.mapLayersEnabled && (
        <Card title="Places" label="Places">
          <ul className="tile-list">
            <li>
              <Link className="tile" to={tripPath(trip.id, "places")}>
                <span className="tile-mark">
                  <MapPin {...ICON} aria-hidden="true" />
                </span>
                <span className="tile-text">
                  <span className="tile-label">Places and categories</span>
                  <span className="tile-hint">
                    Find a place, edit the categories, pull in the Google Maps list
                  </span>
                </span>
                <ChevronRight {...ICON} aria-hidden="true" className="tile-go" />
              </Link>
            </li>
          </ul>
        </Card>
      )}

      <YourSharing trip={trip} />

      <Card title="You" label="You">
        <div className="row wrap">
          <span className="muted small">Signed in as {me.user.displayName}</span>
          <span className="spacer" />
          <button
            type="button"
            className="button small"
            onClick={async () => {
              await signOut();
              window.location.assign("/");
            }}
          >
            <LogOut {...ICON} aria-hidden="true" />
            Sign out
          </button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Switching trips. A native select, so the phone opens its own picker and the control is
 * reachable by keyboard and screen reader without a menu of our own. Names need a fetch per
 * membership — `me.memberships` carries only ids — so the current trip stands alone until they
 * land, and the control never renders empty.
 */
function TripSwitcher({ trip, me }: { trip: Trip; me: Me }) {
  const [trips, setTrips] = useState<{ id: string; name: string }[]>([
    { id: trip.id, name: trip.name },
  ]);

  useEffect(() => {
    let live = true;
    const others = me.memberships.filter((m) => m.tripId !== trip.id);
    if (others.length === 0) return;
    Promise.all(
      others.map((m) =>
        get<Trip>(`/v1/trips/${m.tripId}`)
          .then((t) => ({ id: t.id, name: t.name }))
          .catch(() => null),
      ),
    )
      .then((rows) => {
        if (!live) return;
        const found = rows.filter((r): r is { id: string; name: string } => r !== null);
        setTrips([{ id: trip.id, name: trip.name }, ...found]);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [trip.id, trip.name, me.memberships]);

  return (
    <Card>
      <label className="field trip-switch">
        <span>Trip</span>
        <select value={trip.id} onChange={(e) => navigate(tripPath(e.target.value, "settings"))}>
          {trips.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <div className="row wrap">
        <Link className="button small" to="/">
          <Plane {...ICON} aria-hidden="true" />
          All trips
        </Link>
      </div>
    </Card>
  );
}

/**
 * Each person's own consent, versioned: the call states the version it was based on. Rendered on
 * Overview (the desktop home) and on Settings, which is the only route a phone has to it.
 */
export function YourSharing({ trip }: { trip: Trip }) {
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState<Record<string, boolean | null>>({
    MEDIA: null,
    ROUTE: null,
  });
  useEffect(() => {
    for (const scope of ["MEDIA", "ROUTE"]) {
      get<{ decision: string }>(`/v1/trips/${trip.id}/consents/${scope}`)
        .then((c) => setConsent((x) => ({ ...x, [scope]: c.decision === "GRANTED" })))
        .catch(() => setConsent((x) => ({ ...x, [scope]: false })));
    }
  }, [trip.id]);

  async function toggle(scope: "MEDIA" | "ROUTE") {
    setError(null);
    try {
      const current = await get<{ version: number }>(`/v1/trips/${trip.id}/consents/${scope}`);
      await put(`/v1/trips/${trip.id}/consents/${scope}`, {
        decision: consent[scope] ? "WITHDRAWN" : "GRANTED",
        baseVersion: current.version,
      });
      if (scope === "ROUTE")
        await setPhoneLocation(trip.id, !consent[scope]).catch(() => undefined);
      setConsent((x) => ({ ...x, [scope]: !x[scope] }));
    } catch (e) {
      setError(reason(e as Error));
    }
  }

  return (
    <Card title="Your sharing">
      <p className="muted small" style={{ margin: 0 }}>
        Only footage taken during the trip is shared automatically. If you share your route,
        everyone on this trip can see your full route.
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="row wrap">
        <button
          className="button"
          onClick={() => toggle("MEDIA")}
          disabled={consent["MEDIA"] === null}
        >
          {consent["MEDIA"] ? "Stop sharing my footage" : "Share my footage"}
        </button>
        <button
          className="button"
          onClick={() => toggle("ROUTE")}
          disabled={consent["ROUTE"] === null}
        >
          {consent["ROUTE"] ? "Stop sharing my route" : "Share my route"}
        </button>
      </div>
    </Card>
  );
}
